/**
 * Spaghetti diagram (ADR 0025): the routes of several vehicles over a period on one map, to see flow,
 * crossing points and congested corridors for layout decisions. The thinning runs in SQL
 * (supabase/migrations/20270412000001_spaghetti.sql, report_spaghetti, SECURITY INVOKER, RLS applies); the
 * console only receives thinned line segments per vehicle. This module holds the shared limits, the result
 * shape and a pure copy of the thinning and segment rules (used by the demo mode and tested against the SQL
 * rules). Pure TypeScript.
 */
import { M_PER_DEG } from "./heatmap.js";

export const SPAGHETTI_LIMITS = Object.freeze({
  /** Longest date range (local days, inclusive). SQL: report_check(…, 31). */
  maxDays: 31,
  /** Vehicles × days until a load test on managed Postgres. SQL: fts:report_too_large. */
  maxVehicleDays: 200,
  /** Thinning bucket: one point per vehicle and this many seconds. */
  bucketS: 3,
  /** Point budget of one result; above it every k-th bucket point is kept (`truncated`). */
  maxPoints: 150_000,
  /** Point budget of an export (the 50 000-row cap of a file). */
  exportMaxPoints: 50_000,
  /** The smallest budget the server accepts. */
  minPoints: 1_000,
  /** A gap longer than max(this, 2 × spacing) between kept points breaks the line. */
  gapS: 60,
  /** An implied speed above this between kept points breaks the line (route report jump threshold). */
  jumpKmh: 40,
});

/**
 * One drawn point (ADR 0025 annex): position, time (epoch SECONDS, the first moving point of its 3-s
 * bucket) and reported speed (km/h, 0.1) — the map's hover popup shows the last two.
 */
export type SpaghettiPointTuple = [lat: number, lon: number, t: number, speedKmh: number];

/** One vehicle's route: line segments of drawn points (oldest first), and its totals. */
export type SpaghettiVehicle = {
  id: string;
  name: string;
  segments: SpaghettiPointTuple[][];
  /** Points in the segments (after thinning). */
  points: number;
  /** Moving points read (before thinning). */
  movingPoints: number;
  distanceM: number;
  movingS: number;
};

/** report_spaghetti(). */
export type SpaghettiResult = {
  tz: string;
  bucketS: number;
  /** Every `step`-th bucket point is kept; 1 = one point per bucket. */
  step: number;
  /** ~seconds between drawn points (bucketS × step). */
  spacingS: number;
  maxPoints: number;
  /** The point budget made the server thin beyond one point per bucket. */
  truncated: boolean;
  vehicles: SpaghettiVehicle[];
  totals: { vehicles: number; points: number; movingPoints: number; distanceM: number; movingS: number };
  /** [west, south, east, north] of the drawn points; null without any. */
  bounds: [number, number, number, number] | null;
};

/** Equirectangular distance in metres, as report_spaghetti() computes it between consecutive points. */
export function spaghettiDistanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dx = (lon2 - lon1) * Math.cos((lat2 * Math.PI) / 180);
  return M_PER_DEG * Math.sqrt((lat2 - lat1) ** 2 + dx ** 2);
}

/** A moving point of one vehicle: epoch milliseconds, position and reported speed (km/h). */
export type SpaghettiPoint = { t: number; lat: number; lon: number; speedKmh?: number };

/**
 * One vehicle's moving points → the thinned route, like report_spaghetti(): the first point of each bucket,
 * every `step`-th of those, line breaks at long gaps and jumps, single-point lines dropped. Points must be
 * moving points already (the SQL filter); they are sorted here.
 */
export function spaghettiThin(points: readonly SpaghettiPoint[], step = 1, bucketS: number = SPAGHETTI_LIMITS.bucketS): SpaghettiPointTuple[][] {
  const sorted = [...points].filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon) && !(p.lat === 0 && p.lon === 0)).sort((a, b) => a.t - b.t);
  const firsts: SpaghettiPoint[] = [];
  let prevK: number | null = null;
  for (const p of sorted) {
    const k = Math.floor(p.t / 1000 / bucketS);
    if (k !== prevK) firsts.push(p);
    prevK = k;
  }
  const kept = firsts.filter((_, i) => i % Math.max(1, step) === 0);
  const gap = Math.max(SPAGHETTI_LIMITS.gapS, 2 * bucketS * Math.max(1, step));
  const lines: SpaghettiPoint[][] = [];
  let cur: SpaghettiPoint[] = [];
  for (const p of kept) {
    const prev = cur[cur.length - 1];
    if (prev) {
      const dt = (p.t - prev.t) / 1000;
      const kmh = (spaghettiDistanceM(prev.lat, prev.lon, p.lat, p.lon) / Math.max(dt, 0.001)) * 3.6;
      if (dt > gap || kmh > SPAGHETTI_LIMITS.jumpKmh) {
        lines.push(cur);
        cur = [];
      }
    }
    cur.push(p);
  }
  if (cur.length) lines.push(cur);
  return lines
    .filter((l) => l.length >= 2)
    .map((l) => l.map((p): SpaghettiPointTuple => [p.lat, p.lon, Math.floor(p.t / 1000), Math.round((p.speedKmh ?? 0) * 10) / 10]));
}

/** Thinning step for a point budget, like SQL: k = ceil(buckets / (budget − vehicles)). */
export function spaghettiStep(buckets: number, vehicles: number, maxPoints: number = SPAGHETTI_LIMITS.maxPoints): number {
  return Math.max(1, Math.ceil(buckets / Math.max(1, maxPoints - vehicles)));
}

/** [west, south, east, north] of every point of the vehicles' segments; null without points. */
export function spaghettiBounds(vehicles: readonly Pick<SpaghettiVehicle, "segments">[]): [number, number, number, number] | null {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const v of vehicles)
    for (const seg of v.segments)
      for (const [lat, lon] of seg) {
        w = Math.min(w, lon);
        e = Math.max(e, lon);
        s = Math.min(s, lat);
        n = Math.max(n, lat);
      }
  return Number.isFinite(w) ? [w, s, e, n] : null;
}

/** Number of distinct line colours (tokens --track-1 … --track-N); more vehicles reuse them in order. */
export const SPAGHETTI_COLORS = 10;

/** Colour slot (0-based) of the i-th vehicle of the result (sorted by name, as the server returns them). */
export function spaghettiColorSlot(index: number): number {
  return ((index % SPAGHETTI_COLORS) + SPAGHETTI_COLORS) % SPAGHETTI_COLORS;
}
